import { doc, getDoc } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import Spinner from '../components/Spinner';
import { FaParking, FaShare, FaUser, FaPhone } from "react-icons/fa";
import { IoLocationSharp } from "react-icons/io5";
import { FaBed, FaBath, FaChair, FaMapMarkerAlt } from "react-icons/fa";
import {getAuth} from "firebase/auth"

// Import Swiper components and styles
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import { EffectFade, Autoplay, Navigation, Pagination } from "swiper/modules";
import Contact from '../components/Contact';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';

export default function Listing() {
    const auth = getAuth();
    const params = useParams();
    const navigate = useNavigate();
    const [listing, setListing] = useState(null);
    const [owner, setOwner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [shareLinkCopied, setShareLinkCopied] = useState(false);
    const [contactLandlord, setContactLandlord] = useState(false);

    useEffect(() => {
        async function fetchListing() {
            const docRef = doc(db, "listings", params.listingId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setListing(docSnap.data());
                
                // Fetch owner information
                const ownerRef = doc(db, "users", docSnap.data().userRef);
                const ownerSnap = await getDoc(ownerRef);
                if (ownerSnap.exists()) {
                    setOwner(ownerSnap.data());
                }
                
                setLoading(false);
            }
        }
        fetchListing();
    }, [params.listingId]);

    if (loading) {
        return <Spinner />;
    }

    const isLandProperty = listing.propertyType === "Residential land" || 
                         listing.propertyType === "Agricultural land" || 
                         listing.propertyType === "Commercial land";
    const isCommercialProperty = listing.propertyType === "Commercial Property";
    const isResidential = listing.propertyType === "Houses and Apartments";

    const handleContactOwner = () => {
        navigate(`/contact-owner/${params.listingId}`, { state: { owner } });
    };

    return (
        <main>
            <Swiper 
                slidesPerView={1} 
                navigation 
                pagination={{ type: "progressbar" }}
                effect='fade' 
                modules={[EffectFade, Autoplay, Navigation, Pagination]} 
                autoplay={{ delay: 3000 }}
            >
                {listing.imgUrls.map((url, index) => (
                    <SwiperSlide key={index}>
                        <div 
                            className='relative w-full overflow-hidden h-[300px]'
                            style={{ background: `url(${listing.imgUrls[index]}) center no-repeat`, backgroundSize: "cover" }}
                        ></div>
                    </SwiperSlide>
                ))}
            </Swiper>
            <div className='fixed top-[13%] right-[3%] z-10 bg-white cursor-pointer 
            border-2 border-gray-400 rounded-full w-12 h-12 flex justify-center items-center' onClick={()=>{
                navigator.clipboard.writeText(window.location.href)
                setShareLinkCopied(true);
                setTimeout(()=>{
                setShareLinkCopied(false);
                }, 2000 );
            }}>
            <FaShare className='text-lg text-[#09848B]'/>
            </div>
            {shareLinkCopied && (
                <p className='fixed top-[23%] right-[5%] font-semibold border-2 
                border-gray-400 rounded-md bg-white z-10 p-2'>Link copied, share listing!</p>
            )}

            <div className='m-4 flex flex-col md:flex-row max-w-6xl lg:mx-auto 
            p-4 rounded-lg shadow-lg bg-white lg:space-x-5'>
                <div className='w-full'>
                    <p className='text-2xl font-bold mb-3 text-[#09848B]'>
                        {listing.name}
                    </p>
                    <div className='flex items-center'>
                        <p className='text-xl font-semibold text-red-700'>
                            {listing.offer 
                                ? listing.discountedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") 
                                : listing.regularPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        </p>
                        {listing.currency && (
                            <span className='ml-2 text-gray-600 font-medium'>
                                {listing.currency}
                            </span>
                        )}
                        {listing.type === "rent" && (
                            <span className='ml-1 text-gray-600 font-medium'>
                                / month
                            </span>
                        )}
                    </div>
                    <p className='flex items-center mt-6 mb-3 font-semibold'>
                        <IoLocationSharp className='text-green-700 mr-1'/>
                        {listing.address}
                    </p>
                    <div className='flex justify-start items-center space-x-4 w-[75%]'>
                       <p className='bg-blue-800 w-full max-w-[200px] rounded-md p-1 
                       text-white text-center font-semibold shadow-md'> 
                       {listing.type === "rent" ? "Rent" : "Sale"}
                       </p>
                       {listing.offer && (
                        <p className='w-full max-w-[200px] bg-red-800 rounded-md p-1 
                        text-white text-center font-semibold shadow-md'>
                            {+listing.regularPrice - +listing.discountedPrice} discount
                        </p>
                       )}
                    </div>
                    <p className='mt-3 mb-3'>
                        <span className='font-semibold'>Description - </span>
                        {listing.description}
                    </p>
                    
                    {/* Property Type */}
                    <div className='mb-3'>
                        <span className='font-semibold'>Property Type: </span>
                        <span>{listing.propertyType || "Houses and Apartments"}</span>
                    </div>
                    
                    {/* Property Details */}
                    <ul className='flex flex-wrap items-center gap-2 sm:gap-4 text-sm font-semibold mb-6'>
                        {/* Land Size for Land Properties */}
                        {isLandProperty && listing.landSize && (
                            <li className='flex items-center whitespace-nowrap bg-gray-100 px-3 py-1 rounded-md'>
                                <FaMapMarkerAlt className='text-lg mr-1 text-green-600'/>
                                {listing.landSize}
                            </li>
                        )}
                        
                        {/* Beds and Baths for Residential */}
                        {isResidential && (
                            <>
                                <li className='flex items-center whitespace-nowrap bg-gray-100 px-3 py-1 rounded-md'>
                                    <FaBed className='text-lg mr-1 text-blue-600'/>
                                    {+listing.bedrooms > 1 ? `${listing.bedrooms} Beds` : "1 Bed"}
                                </li>
                                <li className='flex items-center whitespace-nowrap bg-gray-100 px-3 py-1 rounded-md'>
                                    <FaBath className='text-lg mr-1 text-blue-400'/>
                                    {+listing.bathrooms > 1 ? `${listing.bathrooms} Baths` : "1 Bath"}
                                </li>
                            </>
                        )}
                        
                        {/* Parking for Commercial and Residential */}
                        {(isResidential || isCommercialProperty) && (
                            <li className='flex items-center whitespace-nowrap bg-gray-100 px-3 py-1 rounded-md'>
                                <FaParking className='text-lg mr-1 text-[#09848B]'/>
                                {listing.parking ? "Parking spot" : "No parking"}
                            </li>
                        )}
                        
                        {/* Furnished for Residential */}
                        {isResidential && (
                            <li className='flex items-center whitespace-nowrap bg-gray-100 px-3 py-1 rounded-md'>
                                <FaChair className='text-lg mr-1 text-amber-600'/>
                                {listing.furnished ? "Furnished" : "Not furnished"}
                            </li>
                        )}
                    </ul>
                    
                    {listing.userRef !== auth.currentUser?.uid && !contactLandlord && (
                      <div className='mt-6'>
                      <button 
                        onClick={()=>setContactLandlord(true)} 
                        className='px-7 py-3 bg-[#9B870C] text-white font-medium 
                        text-sm uppercase rounded shadow-md hover:bg-[#09848B] hover:shadow-lg 
                        focus:bg-[#09848B] focus:shadow-lg w-full text-center 
                        transition duration-150 ease-in-out'
                      >
                        Book Listing
                      </button>
                      </div>  
                    )}
                    {contactLandlord && (
                        <Contact userRef={listing.userRef} listing={listing}/>
                    )}

                    {/* Listing Owner Information */}
                    {owner && (
                        <div className="mt-8 bg-pink-50 p-6 rounded-lg shadow-md">
                            <h2 className="text-2xl font-semibold mb-4 text-[#09848B]">Listing Owner Information</h2>
                            <div className="space-y-4">
                                <div>
                                    <p className="font-bold text-lg text-[#09848B]">{owner.name}</p>
                                </div>
                                
                                <div className="inline-block bg-gray-100 px-3 py-1 rounded-full">
                                    <p className="text-sm font-medium">{owner.userCategory || "Property Owner"}</p>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                    <FaMapMarkerAlt className="text-gray-500" />
                                    <p>{owner.address}, {owner.country}</p>
                                </div>
                                
                                <div className="flex items-center space-x-2">
                                    <FaPhone className="text-gray-500" />
                                    <p>{owner.phone}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className='w-full h-[250px] md:h-[400px] z-10 overflow-x-hidden mt-6 md:mt-0 md:ml-2'>
                    <MapContainer 
                        center={[listing.geolocation.lat, listing.geolocation.lng]} 
                        zoom={13} 
                        scrollWheelZoom={false}
                        style={{height:"100%", width:"100%"}}
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={[listing.geolocation.lat, listing.geolocation.lng]}>
                            <Popup>
                                {listing.address}
                            </Popup>
                        </Marker>
                    </MapContainer>
                </div>
            </div>
        </main>
    );
}